import React, { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { Panel } from "@/components/common/primitives";
import { CopyValueButton } from "@/components/common/CopyValueButton";
import { api } from "@/lib/api";

/**
 * Read the attributes IziSell already holds for an item.
 *
 * Read-only: the endpoint serves the local cache and never starts a provider
 * request, so opening a details view costs nothing.
 */
export function useWalmartAttributes(itemId) {
  const [attributes, setAttributes] = useState(null);
  const [loading, setLoading] = useState(false);
  const clean = itemId ? String(itemId).replace(/[^0-9A-Za-z]/g, "") : "";

  useEffect(() => {
    let cancelled = false;
    if (!clean) {
      setAttributes(null);
      return () => {};
    }
    setLoading(true);
    api
      .attributes({ itemId: clean })
      .then((res) => {
        if (!cancelled) setAttributes(res?.data ?? res ?? null);
      })
      .catch(() => {
        if (!cancelled) setAttributes(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clean]);

  return { attributes, loading };
}

/**
 * Attributes IziSell already holds for this Walmart item.
 *
 * Three separate facts are shown, never blurred:
 *   observed      a value the provider actually returned (with provenance)
 *   not published a field the provider explicitly does not expose
 *   parse failure a value we received but could not interpret (a bug signal)
 *
 * Fields with no evidence are collapsed into one "not published" line instead
 * of dozens of empty `—` rows.
 */
const GROUP_TITLES = {
  identity: "Identity",
  catalog: "Catalog",
  condition: "Condition",
  reviews: "Ratings",
  fulfillment: "Fulfillment",
  competition: "Competition",
  physical: "Physical",
};

const GROUP_HINTS = {
  physical: "Weight and dimensions gate the WFS fee. Absent values stay absent — they are never estimated.",
  competition: "The Walmart catalog API does not return per-item offer counts for most items.",
  fulfillment: "WFS vs seller-fulfilled is not exposed by the catalog API, so it stays unknown.",
};

function fmtValue(key, value) {
  if (value == null) return "—";
  if (key === "product_dimensions" || key === "package_dimensions") {
    if (typeof value === "object") {
      return `${value.length ?? "?"} × ${value.width ?? "?"} × ${value.height ?? "?"} in`;
    }
  }
  if (key.endsWith("_lb")) return `${Number(value).toFixed(2)} lb`;
  if (key === "rating") return Number(value).toFixed(1);
  if (typeof value === "number") return value.toLocaleString();
  return String(value);
}

function AttributeRow({ field }) {
  const display = fmtValue(field.key, field.value);
  const copyable = display !== "—" && typeof display === "string" && display.length <= 120;
  return (
    <div className="flex items-center justify-between gap-2 text-compact py-1.5 border-b border-edge-subtle">
      <span className="text-ink-secondary min-w-0 truncate" title={field.provenance}>
        {field.label}
      </span>
      <span className="flex items-center gap-1 min-w-0 justify-end">
        <span
          className={cn(
            "text-ink-primary truncate",
            (field.key === "gtin" || field.key === "mpn" || field.key === "model") && "ident text-micro"
          )}
          title={typeof display === "string" ? display : undefined}
        >
          {display}
        </span>
        {copyable && <CopyValueButton value={display} label={field.label} />}
      </span>
    </div>
  );
}

export function WalmartAttributesPanel({ attributes, loading = false, compact = false }) {
  if (loading) {
    return (
      <Panel className="p-4" testid="walmart-attributes">
        <div className="ez-shimmer h-16 rounded" />
      </Panel>
    );
  }
  if (!attributes?.available) {
    return (
      <Panel className="p-4" testid="walmart-attributes">
        <div className="flex items-center justify-between mb-1">
          <span className="text-stitle text-ink-primary">Walmart attributes</span>
          <span className="text-micro font-mono text-ink-muted">not cached</span>
        </div>
        <p className="text-compact text-ink-secondary">
          {attributes?.message || "No attributes cached for this item yet — they are captured on the next authorized lookup."}
        </p>
      </Panel>
    );
  }

  const groups = attributes.groups || {};
  const summary = attributes.summary || {};
  const notPublished = attributes.not_published || [];
  const failures = attributes.parse_failures || [];

  return (
    <Panel className={cn("p-4", compact && "p-3")} testid="walmart-attributes">
      <div className="flex items-center justify-between mb-2 gap-2">
        <span className="text-stitle text-ink-primary">Walmart attributes</span>
        <span className="text-micro text-ink-muted tabnum">
          {summary.observed ?? 0} observed
          {summary.unavailable ? ` · ${summary.unavailable} not published` : ""}
        </span>
      </div>
      {Object.entries(groups).map(([group, fields]) => (
        <div key={group} className="mb-3 last:mb-0" data-testid={`walmart-attributes-${group}`}>
          <div className="text-micro font-medium text-ink-muted uppercase tracking-wide mb-1">{GROUP_TITLES[group] || group}</div>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-0.5">
            {fields.map((f) => (
              <AttributeRow key={f.key} field={f} />
            ))}
          </div>
          {GROUP_HINTS[group] && <p className="text-micro text-ink-muted mt-1">{GROUP_HINTS[group]}</p>}
        </div>
      ))}
      {notPublished.length > 0 && (
        <p className="text-micro text-ink-muted mt-2" data-testid="walmart-attributes-not-published">
          Not published by the provider: {notPublished.map((f) => f.label).join(", ")}.
        </p>
      )}
      {failures.length > 0 && (
        <p className="text-micro text-red-600 mt-1" data-testid="walmart-attributes-parse-failures">
          Could not interpret: {failures.map((f) => `${f.label}${f.raw ? ` (${f.raw})` : ""}`).join(", ")}.
        </p>
      )}
    </Panel>
  );
}

export default WalmartAttributesPanel;
